import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, API_BASE } from "../../lib/api";
import { useBranding, absoluteUrl } from "../../context/BrandingContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Save, Loader2, Upload, Image as ImageIcon, Palette, FileType,
  Globe, Twitter, Trash2, RefreshCw, Languages,
} from "lucide-react";

/**
 * Admin → Branding tab.
 *
 * Sections:
 *  - Identity (name, tagline, footer)
 *  - SEO (meta title/description/keywords)
 *  - Social previews (OG + Twitter cards)
 *  - Visuals (favicon, logo, theme color)
 *  - Live preview (browser tab + share card mockups)
 */
export default function BrandingTab() {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/branding");
      setCfg(data);
      setDraft({});
    } catch {
      toast.error("Failed to load branding settings");
    }
  };
  useEffect(() => { load(); }, []);

  if (!cfg) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const v = (k) => (draft[k] !== undefined ? draft[k] : cfg[k] || "");
  const set = (k, val) => setDraft((d) => ({ ...d, [k]: val }));
  const dirty = Object.keys(draft).length > 0;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/branding", draft);
      setCfg(data);
      setDraft({});
      toast.success("Branding saved");
      window.dispatchEvent(new Event("branding:refresh"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const previewBranding = { ...cfg, ...draft };

  return (
    <div className="space-y-6" data-testid="branding-tab">
      <div className="border border-border bg-card p-6">
        <h3 className="font-display font-bold tracking-tight text-lg">Branding & SEO</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Configure how your application appears in browser tabs, search results, and social share previews. Uploaded images are served from the application — no third-party CDN.
        </p>
      </div>

      <Section icon={<FileType className="h-4 w-4" />} title="Identity">
        <Field label="App name"><Input value={v("app_name")} onChange={(e) => set("app_name", e.target.value)} className="rounded-sm" data-testid="brand-app-name" /></Field>
        <Field label="Tagline (used on the login page)">
          <Textarea value={v("tagline")} onChange={(e) => set("tagline", e.target.value)} rows={2} className="rounded-sm" data-testid="brand-tagline" />
        </Field>
        <Field label="Footer text (small print under tagline)">
          <Input value={v("footer_text")} onChange={(e) => set("footer_text", e.target.value)} className="rounded-sm" data-testid="brand-footer" />
        </Field>
      </Section>

      <Section icon={<Languages className="h-4 w-4" />} title="Default language">
        <Field
          label="Default language for new visitors"
          hint="What anonymous visitors and brand-new accounts see on their first page load. Existing users keep their own saved preference."
        >
          <select
            value={v("default_locale") || "es-AR"}
            onChange={(e) => set("default_locale", e.target.value)}
            className="h-10 px-3 rounded-sm border border-border bg-background text-sm w-full max-w-xs font-mono"
            data-testid="brand-default-locale"
          >
            <option value="es-AR">Español (Argentina)</option>
            <option value="en">English</option>
          </select>
        </Field>
      </Section>

      <Section icon={<Globe className="h-4 w-4" />} title="SEO (search engines)">
        <Field label="Meta title" hint="Shown in browser tabs and search results">
          <Input value={v("meta_title")} onChange={(e) => set("meta_title", e.target.value)} className="rounded-sm" data-testid="brand-meta-title" />
          <CharCount value={v("meta_title")} max={60} ideal={60} />
        </Field>
        <Field label="Meta description" hint="Shown in search results under the title">
          <Textarea value={v("meta_description")} onChange={(e) => set("meta_description", e.target.value)} rows={3} className="rounded-sm" data-testid="brand-meta-desc" />
          <CharCount value={v("meta_description")} max={160} ideal={160} />
        </Field>
        <Field label="Meta keywords" hint="Comma-separated, lowercase">
          <Input value={v("meta_keywords")} onChange={(e) => set("meta_keywords", e.target.value)} className="rounded-sm font-mono text-sm" data-testid="brand-meta-keywords" />
        </Field>
      </Section>

      <Section icon={<Twitter className="h-4 w-4" />} title="Social share previews (Open Graph + Twitter)">
        <Field label="OG title" hint="Leave blank to reuse meta title">
          <Input value={v("og_title")} onChange={(e) => set("og_title", e.target.value)} placeholder={cfg.meta_title} className="rounded-sm" data-testid="brand-og-title" />
        </Field>
        <Field label="OG description" hint="Leave blank to reuse meta description">
          <Textarea value={v("og_description")} onChange={(e) => set("og_description", e.target.value)} rows={2} placeholder={cfg.meta_description} className="rounded-sm" data-testid="brand-og-desc" />
        </Field>
        <Field label="OG canonical URL" hint="e.g. https://yourapp.com">
          <Input value={v("og_url")} onChange={(e) => set("og_url", e.target.value)} className="rounded-sm font-mono text-sm" placeholder="https://yourapp.com" data-testid="brand-og-url" />
        </Field>
        <Field label="Share image (1200×630 recommended)">
          <ImageUploader kind="og_image" currentUrl={v("og_image_url")} onUploaded={(url) => { set("og_image_url", url); load(); }} testId="upload-og-image" />
        </Field>
        <Field label="Twitter card type">
          <select
            value={v("twitter_card") || "summary_large_image"}
            onChange={(e) => set("twitter_card", e.target.value)}
            className="h-10 px-3 rounded-sm border border-border bg-background text-sm w-full font-mono"
            data-testid="brand-twitter-card"
          >
            <option value="summary">summary (small image)</option>
            <option value="summary_large_image">summary_large_image (big image)</option>
          </select>
        </Field>
        <Field label="Twitter site handle">
          <Input value={v("twitter_site")} onChange={(e) => set("twitter_site", e.target.value)} placeholder="@yourbrand" className="rounded-sm font-mono text-sm" data-testid="brand-twitter-site" />
        </Field>
        <Field label="Twitter image (optional, falls back to share image)">
          <ImageUploader kind="twitter_image" currentUrl={v("twitter_image_url")} onUploaded={(url) => { set("twitter_image_url", url); load(); }} testId="upload-twitter-image" />
        </Field>
      </Section>

      <Section icon={<Palette className="h-4 w-4" />} title="Visual identity">
        <Field label="Favicon" hint=".ico, .png, or .svg — 32×32 minimum">
          <ImageUploader kind="favicon" currentUrl={v("favicon_url")} onUploaded={(url) => { set("favicon_url", url); load(); }} previewSize="sm" testId="upload-favicon" />
        </Field>
        <Field label="Logo" hint="Used in the app header. PNG or SVG with transparent background recommended">
          <ImageUploader kind="logo" currentUrl={v("logo_url")} onUploaded={(url) => { set("logo_url", url); load(); }} testId="upload-logo" />
        </Field>
        <Field label="Theme color" hint="Browser chrome on mobile">
          <div className="flex items-center gap-2">
            <input type="color" value={v("theme_color") || "#000000"} onChange={(e) => set("theme_color", e.target.value)} className="h-10 w-16 rounded-sm border border-border cursor-pointer" data-testid="brand-theme-color" />
            <Input value={v("theme_color")} onChange={(e) => set("theme_color", e.target.value)} className="rounded-sm font-mono text-sm flex-1" placeholder="#000000" />
          </div>
        </Field>
      </Section>

      <LivePreview branding={previewBranding} />

      <div className="sticky bottom-0 -mx-1 px-1 py-3 bg-background border-t border-border flex items-center gap-2 flex-wrap">
        <Button onClick={save} disabled={!dirty || saving} className="rounded-sm" data-testid="branding-save">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
          {saving ? "Saving…" : "Save branding"}
        </Button>
        {dirty && <span className="text-xs font-mono text-amber-600 dark:text-amber-400">unsaved changes</span>}
        <Button variant="ghost" onClick={load} disabled={saving} className="rounded-sm ml-auto"><RefreshCw className="h-3.5 w-3.5 mr-1.5" />Reload</Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function Section({ icon, title, children }) {
  return (
    <div className="border border-border bg-card rounded-sm p-5 space-y-4">
      <div className="flex items-center gap-2 pb-2 border-b border-border">
        {icon}
        <h4 className="font-display font-bold tracking-tight">{title}</h4>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider font-mono">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="text-[11px] text-muted-foreground mt-1 font-mono">{hint}</p>}
    </div>
  );
}

function CharCount({ value, max, ideal }) {
  const len = (value || "").length;
  const over = len > max;
  const close = len > max * 0.9;
  const cls = over ? "text-destructive" : close ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground";
  return <div className={`text-[10px] font-mono mt-1 ${cls}`} data-testid="char-count">{len} / {ideal}</div>;
}

function ImageUploader({ kind, currentUrl, onUploaded, previewSize = "md", testId }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/branding/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded");
      onUploaded?.(data.url);
      window.dispatchEvent(new Event("branding:refresh"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const sizes = previewSize === "sm" ? "h-10 w-10" : "h-16 w-16";

  return (
    <div className="flex items-center gap-3" data-testid={testId}>
      <div className={`${sizes} flex-shrink-0 border border-border rounded-sm bg-muted/30 grid place-items-center overflow-hidden`}>
        {currentUrl ? (
          <img src={absoluteUrl(currentUrl)} alt="" className="max-h-full max-w-full object-contain" />
        ) : (
          <ImageIcon className="h-5 w-5 text-muted-foreground" />
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/*" onChange={(e) => handleFile(e.target.files?.[0])} className="hidden" />
      <div className="flex flex-col gap-1 min-w-0 flex-1">
        <div className="flex gap-1">
          <Button size="sm" variant="outline" type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-sm h-8">
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}
            {uploading ? "Uploading…" : currentUrl ? "Replace" : "Upload"}
          </Button>
          {currentUrl && (
            <Button size="sm" variant="ghost" type="button" onClick={() => onUploaded?.("")} className="rounded-sm h-8 text-muted-foreground hover:text-destructive">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        {currentUrl && <code className="text-[10px] font-mono text-muted-foreground truncate">{currentUrl}</code>}
      </div>
    </div>
  );
}

function LivePreview({ branding }) {
  const title = branding.meta_title || "App Title";
  const desc = branding.meta_description || "App description goes here.";
  const ogTitle = branding.og_title || title;
  const ogDesc = branding.og_description || desc;
  const ogImg = absoluteUrl(branding.og_image_url);
  const ogUrl = branding.og_url || "yourapp.com";
  const fav = absoluteUrl(branding.favicon_url);

  return (
    <div className="border border-border bg-card rounded-sm p-5 space-y-4" data-testid="branding-preview">
      <div className="flex items-center gap-2 pb-2 border-b border-border">
        <Globe className="h-4 w-4" />
        <h4 className="font-display font-bold tracking-tight">Live preview</h4>
        <span className="text-[11px] font-mono text-muted-foreground">— reflects unsaved edits</span>
      </div>

      {/* Browser tab mock */}
      <div>
        <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-2">Browser tab</div>
        <div className="inline-flex items-center gap-2 max-w-md bg-muted/40 border border-border rounded-t-md px-3 py-1.5">
          {fav ? <img src={fav} alt="" className="h-4 w-4" /> : <div className="h-4 w-4 bg-muted-foreground/30 rounded-sm" />}
          <span className="text-xs truncate">{title}</span>
        </div>
      </div>

      {/* Facebook / LinkedIn card mock */}
      <div>
        <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-2">Facebook / LinkedIn share</div>
        <div className="max-w-md border border-border rounded-sm overflow-hidden bg-background">
          {ogImg ? (
            <img src={ogImg} alt="" className="w-full aspect-[1.91/1] object-cover" />
          ) : (
            <div className="w-full aspect-[1.91/1] bg-muted grid place-items-center text-xs font-mono text-muted-foreground">no share image uploaded</div>
          )}
          <div className="p-3 border-t border-border">
            <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">{ogUrl}</div>
            <div className="font-semibold text-sm mt-0.5 line-clamp-1">{ogTitle}</div>
            <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{ogDesc}</div>
          </div>
        </div>
      </div>

      {/* Google search mock */}
      <div>
        <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-2">Google search result</div>
        <div className="max-w-xl">
          <div className="text-xs text-muted-foreground font-mono">{ogUrl || "yourapp.com"}</div>
          <div className="text-blue-600 dark:text-blue-400 text-lg font-medium mt-0.5 truncate">{title}</div>
          <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{desc}</div>
        </div>
      </div>
    </div>
  );
}
